/** An imported figure's receipt, drawn as the same chain a calculated figure's is (gate 22).
 *
 * The receipt of a figure somebody else calculated used to be a paragraph and four labelled
 * fields. It named a source in a sentence, never linked the document it was asserted from, and
 * showed no ledger entry at all - so an auditor following a calculated figure down the chain and an
 * imported one found two different kinds of page. This draws the second with the first's frame
 * (`ReceiptChain`): the same rail, the same three standings, the same break when a link is
 * missing, so the difference between the species is carried by *which links apply* and not by how
 * the page looks.
 *
 * **What it must keep from the species** (ADR-0032): no factor, no engine version, no activity
 * record. The calculation node says *not applicable* in words. The labelled fields in the panel
 * above stay exactly as they were - `ui-check` reads their labels to assert that absence over
 * structure - and nothing here uses a `dt`.
 */

import { Link } from "@tanstack/react-router";

import { useReader } from "../api/profile";
import { instantDate, shortHash } from "../lib/format";
import { keepSearch } from "../screens/search";
import { buildImportedChain } from "./importedLinks";
import type { ImportedChainReceipt } from "./importedLinks";
import { ChainLink, ReceiptChain } from "./ReceiptChain";
import styles from "./ImportedChain.module.css";

export function ImportedChain({ receipt }: { receipt: ImportedChainReceipt }) {
  const reader = useReader();
  const nodes = buildImportedChain(receipt);
  return (
    <div className={styles.wrap} data-testid="imported-chain">
      <ReceiptChain nodes={nodes}>
        {nodes.map((node) => (
          <ChainLink key={node.key} node={node}>
            {node.key === "source" && receipt.source_document ? (
              <div className={styles.kv}>
                <span className={styles.k}>Document</span>
                <span className={styles.v}>
                  <Link
                    to="/evidence/$documentId"
                    params={{ documentId: receipt.source_document.id }}
                    search={keepSearch}
                    className={styles.link}
                    data-testid="imported-source-link"
                  >
                    {receipt.source_document.original_filename ?? "Untitled document"}
                  </Link>
                </span>
                <span className={styles.k}>State</span>
                <span className={styles.v}>
                  {receipt.source_document.state.replace(/_/g, " ")}
                  {receipt.source_document.retention_expired ? (
                    <span className={styles.missing}> · retention expired</span>
                  ) : null}
                </span>
              </div>
            ) : null}
            {node.key === "assertion" ? (
              <div className={styles.kv}>
                <span className={styles.k}>Asserted</span>
                <span className={styles.v}>{instantDate(receipt.asserted_at, reader)}</span>
                {/* A person's id and never a name: the chain and everything it touches hold
                    identifiers only (rule 4). Who this is stays a question for the workspace's
                    People list, which an engaged auditor is deliberately not shown (B-02). */}
                <span className={styles.k}>By</span>
                <span className={`${styles.v} ${styles.mono}`} data-testid="imported-asserted-by">
                  {receipt.asserted_by}
                </span>
              </div>
            ) : null}
            {node.key === "ledger"
              ? receipt.ledger.map((entry) => (
                  <div key={entry.entry_id} className={styles.kv} data-testid="imported-ledger-entry">
                    <span className={styles.k}>Entry</span>
                    <span className={styles.v}>
                      #{entry.seq} · {entry.type.replace(/[._]/g, " ")}
                    </span>
                    <span className={styles.k}>Hash</span>
                    <span className={`${styles.v} ${styles.mono}`}>{shortHash(entry.hash)}</span>
                    <span className={styles.k}>Previous</span>
                    <span className={`${styles.v} ${styles.mono}`}>{shortHash(entry.prev_hash)}</span>
                  </div>
                ))
              : null}
          </ChainLink>
        ))}
      </ReceiptChain>
    </div>
  );
}
